"use client";

import { debugLog, debugWarn } from "@/utils/logging";
import { createSupabaseClientForBrowser } from "@/utils/supabase/client";
import { Session } from "@supabase/supabase-js";
import { isServer, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "jotai";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { createContext, ReactNode, useActionState, useContext, useEffect, useRef, useState } from "react";
import { getUserOrAnon } from "./serverActions";
import { NuqsAdapter } from "nuqs/adapters/next/app";
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 5 * 60 * 1000 // 5 minutes
      }
    }
  });
}
let browserQueryClient: QueryClient | undefined = undefined;
function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}
const ReactQueryProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();
  return <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-component="ReactQueryProvider" data-sentry-source-file="providers.tsx">{children}</QueryClientProvider>;
};
if (typeof window !== "undefined" && !process.env.NEXT_PUBLIC_DISABLE_POSTHOG) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY || "", {
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    session_recording: {
      maskAllInputs: false
    },
    enable_recording_console_log: true
  });
}
function CSPostHogProvider({
  children
}: {
  children: ReactNode;
}) {
  return <PostHogProvider client={posthog} data-sentry-element="PostHogProvider" data-sentry-component="CSPostHogProvider" data-sentry-source-file="providers.tsx">{children}</PostHogProvider>;
}
const JotaiProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  return <Provider data-sentry-element="Provider" data-sentry-component="JotaiProvider" data-sentry-source-file="providers.tsx">{children}</Provider>;
};
export const SupabaseAuthContext = createContext<{
  session: Session | null;
  retrieveSession: () => Promise<Session | null>;
  isLoadingSession: boolean;
}>({
  session: null,
  retrieveSession: async () => null,
  isLoadingSession: false
});
const SupabaseAuthProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [session, setSession] = useState<Session | null>(null); // TODO: @zain, IDEALLY this would be localStorage for user changes to be synced in the browser across tabs
  const supabase = createSupabaseClientForBrowser();
  const sessionBroadcaster = useRef<{
    broadcast: (session: Session | null) => void;
    _lastSessionSet: Session | null;
    _lastSessionSetTime: number;
    _resolve: (session: Session) => void;
    _reject: () => void;
    _promise: Promise<Session | null>;
  }>({
    broadcast: (session: Session | null) => {
      debugLog("Broadcasting session:", session);
      sessionBroadcaster.current._lastSessionSet = session;
      sessionBroadcaster.current._lastSessionSetTime = Date.now();
      if (session) {
        sessionBroadcaster.current._resolve(session);
        sessionBroadcaster.current._resolve = () => {};
      } else {
        sessionBroadcaster.current._reject();
      }
    },
    _lastSessionSet: null,
    _lastSessionSetTime: 0,
    _resolve: (session: Session) => {},
    _reject: () => {},
    _promise: null as any
  });
  const [fetchedSession, action_getUserOrAnon, pending] = useActionState(getUserOrAnon, null); // TODO: ideally, the PENDING state should be synced as well for other tabs not to start the same process as the session would be shared on successful retrieval

  useEffect(() => {
    if (fetchedSession) {
      debugLog("setting fetched session:", fetchedSession);
      supabase.auth.setSession(fetchedSession);
    }
  }, [fetchedSession]);
  const doRetrieveSession = async () => {
    const minCacheTime = 2200;
    if (sessionBroadcaster.current._lastSessionSet && Date.now() - sessionBroadcaster.current._lastSessionSetTime < minCacheTime) {
      debugLog("Session already set, returning existing");
      return sessionBroadcaster.current._lastSessionSet;
    }
    action_getUserOrAnon(); // this will set the cookie / ensure it

    sessionBroadcaster.current._promise = new Promise<Session | null>((resolve, reject) => {
      sessionBroadcaster.current._resolve = resolve;
      sessionBroadcaster.current._reject = reject;
    });
    return sessionBroadcaster.current._promise;
  };
  const retrieveSession = async () => {
    let sessionPromise: Promise<Session | null> = sessionBroadcaster.current._promise;
    if (!pending) {
      debugLog("Session retrieval not in progress, starting");
      sessionPromise = doRetrieveSession();
    } else {
      debugWarn("Session retrieval already in progress, got re-triggered, returning existing");
    }
    return sessionPromise;
  };
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      debugLog("Event:", event, "User:", session);
      if (event === "SIGNED_OUT") {
        setSession(null);
      } else if (session) {
        setSession(session);
        debugLog("User Session retrieved:", session);
      }
      sessionBroadcaster.current.broadcast(session);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  return <SupabaseAuthContext.Provider value={{
    session,
    retrieveSession,
    isLoadingSession: pending
  }} data-sentry-element="unknown" data-sentry-component="SupabaseAuthProvider" data-sentry-source-file="providers.tsx">
      {children}
    </SupabaseAuthContext.Provider>;
};
const SessionLoaderDialog = () => {
  const {
    isLoadingSession
  } = useContext(SupabaseAuthContext);
  const [isOpen, setIsOpen] = useState(isLoadingSession);
  const [openTime, setOpenTime] = useState<number>(Date.now());
  const timeoutRef = useRef<number | null>(null);
  useEffect(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    if (isLoadingSession) {
      setIsOpen(true);
      setOpenTime(Date.now());
      return;
    }
    const minWait = 800;
    const openDuration = Date.now() - openTime;
    if (openDuration < minWait) {
      timeoutRef.current = window.setTimeout(() => {
        setIsOpen(false);
      }, minWait - openDuration);
    } else {
      setIsOpen(false);
    }
  }, [isLoadingSession]);
  return <></>;
};
export const Providers = ({
  children
}: {
  children: ReactNode;
}) => {
  return <JotaiProvider data-sentry-element="JotaiProvider" data-sentry-component="Providers" data-sentry-source-file="providers.tsx">
      <NuqsAdapter data-sentry-element="NuqsAdapter" data-sentry-source-file="providers.tsx">
        <SupabaseAuthProvider data-sentry-element="SupabaseAuthProvider" data-sentry-source-file="providers.tsx">
          <ReactQueryProvider data-sentry-element="ReactQueryProvider" data-sentry-source-file="providers.tsx">
            <CSPostHogProvider data-sentry-element="CSPostHogProvider" data-sentry-source-file="providers.tsx">
              {children}

              <SessionLoaderDialog data-sentry-element="SessionLoaderDialog" data-sentry-source-file="providers.tsx" />
            </CSPostHogProvider>
          </ReactQueryProvider>
        </SupabaseAuthProvider>
      </NuqsAdapter>
    </JotaiProvider>;
};